'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TopbarProps {
  title: string;
  subtitle?: string;
}

const notifications = [
  { id: 'notif-001', type: 'alert', message: 'Chicken Wings stock critically low — 12 cases remaining', time: '4m ago' },
  { id: 'notif-002', type: 'warning', message: 'Invoice #INV-2847 overdue — Epicurean Food Hall XCD $14,200', time: '22m ago' },
  { id: 'notif-003', type: 'info', message: 'PO #PO-0391 received — 48 pallets from CaribbeanFoods Ltd', time: '1h ago' },
  { id: 'notif-004', type: 'success', message: 'Delivery #DEL-1103 completed — Courts Megastore', time: '2h ago' },
  { id: 'notif-005', type: 'warning', message: 'ERPLY sync failed — 2 products not updated', time: '3h ago' },
];

const typeIcon: Record<string, string> = {
  alert: 'ExclamationTriangleIcon',
  warning: 'ExclamationCircleIcon',
  info: 'InformationCircleIcon',
  success: 'CheckCircleIcon',
};
const typeColor: Record<string, string> = {
  alert: 'text-negative',
  warning: 'text-warning',
  info: 'text-info',
  success: 'text-positive',
};

export default function Topbar({ title, subtitle }: TopbarProps) {
  const [showNotifications, setShowNotifications] = useState(false);
  const [erplyStatus] = useState<'synced' | 'syncing' | 'error'>('syncing');

  const erplyColors: Record<string, string> = {
    synced: 'bg-positive/10 text-positive border-positive/20',
    syncing: 'bg-warning/10 text-warning border-warning/20',
    error: 'bg-negative/10 text-negative border-negative/20',
  };
  const erplyLabels: Record<string, string> = {
    synced: 'ERPLY Synced',
    syncing: 'ERPLY Syncing…',
    error: 'ERPLY Error',
  };

  return (
    <header className="h-16 bg-card border-b border-border flex items-center px-6 gap-4 sticky top-0 z-20">
      {/* Title */}
      <div className="flex-1 min-w-0">
        <h1 className="text-lg font-700 text-foreground truncate">{title}</h1>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>

      {/* ERPLY sync badge */}
      <div className={`hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-600 ${erplyColors[erplyStatus]}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${erplyStatus === 'syncing' ? 'bg-warning animate-pulse' : erplyStatus === 'synced' ? 'bg-positive' : 'bg-negative'}`} />
        {erplyLabels[erplyStatus]}
      </div>

      {/* WhatsApp notifications */}
      <button className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-positive/10 border border-positive/20 text-xs font-600 text-positive hover:bg-positive/20 transition-colors">
        <Icon name="ChatBubbleLeftRightIcon" size={14} className="text-positive" />
        <span>12 queued</span>
      </button>

      {/* Date */}
      <span className="hidden lg:block text-xs text-muted-foreground font-500">
        Thu, 31 Jul 2026
      </span>

      {/* Notifications bell */}
      <div className="relative">
        <button
          onClick={() => setShowNotifications(!showNotifications)}
          className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-muted transition-colors"
          aria-label="Notifications"
        >
          <Icon name="BellIcon" size={20} className="text-muted-foreground" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-negative rounded-full" />
        </button>

        {showNotifications && (
          <div className="absolute right-0 top-11 w-80 bg-card border border-border rounded-xl shadow-card-lg z-50 fade-in">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <span className="text-sm font-600 text-foreground">Notifications</span>
              <button className="text-xs text-primary font-600 hover:underline">Mark all read</button>
            </div>
            <div className="divide-y divide-border max-h-72 overflow-y-auto scrollbar-thin">
              {notifications.map((n) => (
                <div key={n.id} className="flex gap-3 px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer">
                  <Icon
                    name={typeIcon[n.type] as Parameters<typeof Icon>[0]['name']}
                    size={16}
                    className={`${typeColor[n.type]} shrink-0 mt-0.5`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground leading-snug">{n.message}</p>
                    <p className="text-2xs text-muted-foreground mt-0.5">{n.time}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2 border-t border-border">
              <button className="text-xs text-primary font-600 hover:underline">View all notifications</button>
            </div>
          </div>
        )}
      </div>

      {/* Search */}
      <button className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-muted transition-colors" aria-label="Search">
        <Icon name="MagnifyingGlassIcon" size={20} className="text-muted-foreground" />
      </button>
    </header>
  );
}